// Legal content for all Coly legal pages.
// Stored as markdown strings; rendered with react-markdown in <LegalPage>.

export const PRIVACY_POLICY = `# Coly – Privacy Policy

## Professional Health-Tech Privacy Policy

Effective Date: May 8, 2026
Last Updated: May 8, 2026

This Privacy Policy describes how Coly ("Coly", "we", "our", or "us") collects, uses, stores, processes, protects, and discloses personal information when users access or use the Coly mobile application, website, and related services.

Coly is a personal wellness and symptom tracking platform focused on inflammatory bowel diseases (IBD), including Crohn's disease and ulcerative colitis.

By accessing or using Coly, users acknowledge and agree to the practices described in this Privacy Policy.

---

# 1. Data Controller

Controller: Hillel Primack
Country: Belgium
Website: [https://colyapp.com/](https://colyapp.com/)
Email: [support@colyapp.com](mailto:support@colyapp.com)

---

# 2. Definitions

For purposes of this Privacy Policy:

* "App" refers to the Coly mobile application.
* "Personal Data" means information relating to an identified or identifiable person.
* "Sensitive Health Data" refers to health-related information voluntarily submitted by users.
* "Processing" means collection, storage, use, disclosure, deletion, or other handling of information.
* "User" means any individual using Coly.
* "GDPR" refers to the General Data Protection Regulation (EU) 2016/679.

---

# 3. Categories of Information We Collect

## 3.1 Account Information

We may collect:

* Name
* Email address
* Encrypted password credentials

## 3.2 Health & Wellness Information

Users may voluntarily provide:

* Symptom tracking information
* Stool and bowel movement tracking
* Medication tracking
* Food diary information
* Wellness notes
* Health-related routines
* Reminder preferences
* AI-generated insight history

## 3.3 Personal Information

We may collect:

* Age
* Height
* Weight

## 3.4 Technical Information

We may automatically collect:

* Device type
* Device operating system
* Application version
* Crash diagnostics
* Error reports
* Technical logs

---

# 4. Special Category Health Data

Certain information processed by Coly may qualify as special category health-related data under GDPR and other privacy laws.

Users explicitly consent to the processing of voluntarily submitted health-related information for wellness tracking purposes.

Coly processes health-related data solely for:

* User-requested tracking
* Personal analytics
* Wellness monitoring
* AI-powered informational summaries
* Historical symptom analysis
* App functionality requested by the user

Coly does not:

* Sell health data
* Use health data for advertising
* Share health information with advertisers
* Share health data with data brokers
* Use health data for unrelated profiling

---

# 5. Legal Bases for Processing (GDPR)

Depending on the circumstances, we process information under one or more lawful bases, including:

## 5.1 Consent

Users voluntarily provide health-related data and consent to its processing.

## 5.2 Performance of Services

Processing may be necessary to provide app functionality requested by users.

## 5.3 Legitimate Interests

We may process limited technical information to:

* Maintain app security
* Improve platform stability
* Detect abuse or fraud
* Resolve technical issues

## 5.4 Legal Obligations

Certain processing may occur where required by law.

---

# 6. How We Use Information

We may use collected information to:

* Provide app functionality
* Enable symptom and wellness tracking
* Generate user-requested analytics
* Provide AI-generated insights
* Improve app performance
* Respond to support requests
* Monitor stability and security
* Detect abuse or unauthorized activity
* Comply with legal obligations

---

# 7. AI Features & Automated Insights

Coly may provide AI-generated suggestions, summaries, informational wellness insights, or automated observations.

These outputs:

* Are informational only
* May contain inaccuracies
* Are not guaranteed to be complete or medically correct
* Should not replace professional medical advice

Users remain solely responsible for healthcare decisions.

---

# 8. Medical Disclaimer

Coly is not a medical device.

Coly does not provide:

* Medical diagnosis
* Clinical treatment
* Emergency assistance
* Professional medical advice

The app is intended solely for personal wellness tracking and informational purposes.

Users should always consult qualified healthcare professionals regarding medical concerns.

---

# 9. Data Sharing & Disclosure

We do not sell personal information.

We may disclose limited information:

* To infrastructure providers necessary for app functionality
* When legally required
* To protect security or legal rights
* During business transfers or restructuring

Third-party providers are contractually expected to process information only for authorized purposes.

---

# 10. Third-Party Infrastructure Providers

Coly currently uses:

* MongoDB

Third-party providers may process limited information required for hosting and infrastructure services.

Additional providers may be introduced in the future, including:

* Authentication providers
* Analytics providers
* Payment processors
* Push notification providers

If services materially change, this Privacy Policy may be updated.

---

# 11. Data Retention

We retain information only for as long as reasonably necessary to:

* Provide services
* Maintain security
* Comply with legal obligations
* Resolve disputes
* Enforce agreements

Deleted information may temporarily remain in backups before permanent deletion.

---

# 12. Account Deletion & Data Removal

Users may delete their account directly within the app.

Following deletion:

* Personal information may be removed
* Health-related tracking information may be deleted
* Associated user records may be permanently erased within a reasonable timeframe unless retention is legally required

Users may also request deletion assistance via:

[support@colyapp.com](mailto:support@colyapp.com)

---

# 13. International Transfers

Coly is available worldwide.

Information may be processed or stored outside a user's country of residence.

Where applicable, we implement reasonable safeguards intended to comply with applicable privacy laws.

---

# 14. Security Measures

We implement commercially reasonable technical and organizational safeguards designed to protect information.

Security measures may include:

* Access controls
* Infrastructure protections
* Authentication safeguards
* Database security controls
* Technical monitoring
* Encrypted communications where applicable

No system can guarantee absolute security.

Users acknowledge risks associated with internet-based services.

---

# 15. User Rights (GDPR)

Users located in the European Economic Area may have rights including:

* Right of access
* Right to correction
* Right to deletion
* Right to portability
* Right to object
* Right to restrict processing
* Right to withdraw consent
* Right to lodge complaints with supervisory authorities

Requests may be submitted to:

[support@colyapp.com](mailto:support@colyapp.com)

---

# 16. Children's Privacy

Coly is not specifically directed toward children under the minimum legal digital consent age applicable in their jurisdiction.

If we become aware that information has been collected without required consent, we may remove such information.

---

# 17. Data Breach Response

In the event of a security incident affecting personal information, we may take actions required under applicable laws, including:

* Investigating incidents
* Securing systems
* Notifying affected users where legally required
* Cooperating with authorities where appropriate

---

# 18. Changes to This Privacy Policy

We may modify this Privacy Policy periodically.

Updated versions become effective when published.

Continued use of Coly after updates constitutes acceptance of revised terms.

---

# 19. Contact Information

For privacy-related questions:

[support@colyapp.com](mailto:support@colyapp.com)
`;

export const TERMS = `# Coly – Terms & Conditions

## Professional Health-Tech Terms of Use

Effective Date: May 8, 2026
Last Updated: May 8, 2026

These Terms & Conditions ("Terms") govern access to and use of the Coly mobile application, website, and related services.

By using Coly, users agree to these Terms.

If users do not agree, they should discontinue use of the services.

---

# 1. About Coly

Coly is a wellness and symptom tracking platform intended to assist users in monitoring personal wellness information related to inflammatory bowel diseases (IBD), including Crohn's disease and ulcerative colitis.

Coly is not a medical provider or healthcare service.

---

# 2. Eligibility

Users must comply with applicable laws regarding use of digital services.

By using Coly, users represent that:

* Information submitted is accurate
* They will use the app lawfully
* They will not misuse the services

---

# 3. User Accounts

Users may create accounts using email and password authentication.

Users are responsible for:

* Maintaining password confidentiality
* Securing their devices
* Activities occurring under their account

Users must immediately notify us regarding suspected unauthorized access.

---

# 4. Health Information

Users voluntarily provide health-related tracking information.

Users remain responsible for the accuracy and legality of submitted information.

Coly processes submitted information solely for providing requested app functionality.

---

# 5. No Medical Advice

Coly does not provide:

* Medical diagnosis
* Clinical treatment
* Emergency response
* Professional healthcare services

AI-generated suggestions and wellness insights are informational only.

Users should always consult licensed healthcare professionals regarding medical decisions.

---

# 6. AI Services Disclaimer

Coly may provide automated AI-powered insights, summaries, trends, or informational recommendations.

AI-generated outputs:

* May contain inaccuracies
* Are not guaranteed to be correct
* Should not replace professional medical advice
* Are used at the user's own discretion

---

# 7. Acceptable Use

Users agree not to:

* Abuse the platform
* Attempt unauthorized access
* Reverse engineer the application
* Distribute malware
* Interfere with infrastructure
* Use the app unlawfully
* Upload illegal or harmful content

We reserve the right to suspend or terminate accounts violating these Terms.

---

# 8. User Content & Responsibility

Users remain solely responsible for information uploaded or entered into Coly.

Users agree not to upload:

* False medical claims
* Malicious code
* Illegal material
* Content violating third-party rights

---

# 9. Intellectual Property

All app functionality, branding, software, visual elements, logos, and related intellectual property belong to Coly and its owner unless otherwise stated.

Users may not copy, distribute, reproduce, or exploit protected materials without authorization.

---

# 10. Future Paid Features

Coly may introduce future premium subscriptions, paid services, or optional purchases.

If payments are introduced:

* Additional terms may apply
* Pricing will be disclosed before purchase
* Billing terms may be governed by app store providers

---

# 11. Service Availability

We may update, modify, suspend, or discontinue portions of the services at any time.

We do not guarantee uninterrupted or error-free operation.

---

# 12. Limitation of Liability

To the maximum extent permitted by law:

Coly and its owner shall not be liable for:

* Medical outcomes
* Healthcare decisions
* AI inaccuracies
* Data loss
* Service interruptions
* Indirect damages
* User-entered inaccuracies
* Technical failures beyond reasonable control

Users assume responsibility for their use of the app.

---

# 13. Indemnification

Users agree to indemnify and hold harmless Coly and its owner against claims, liabilities, losses, damages, or expenses arising from:

* Violation of these Terms
* Misuse of the services
* Violation of applicable laws
* Reliance on AI-generated outputs

---

# 14. Termination

We may suspend or terminate accounts violating these Terms or applicable laws.

Users may stop using Coly and delete their accounts at any time.

---

# 15. Privacy

Use of Coly is additionally governed by the Coly Privacy Policy.

---

# 16. Governing Law & Jurisdiction

These Terms are governed by the laws of Belgium.

Applicable disputes shall be subject to competent courts under Belgian law unless otherwise required by applicable consumer protection laws.

---

# 17. Severability

If any provision of these Terms is found unenforceable, remaining provisions shall remain in effect.

---

# 18. Force Majeure

We shall not be liable for delays or failures caused by circumstances beyond reasonable control, including:

* Internet outages
* Infrastructure failures
* Cyberattacks
* Government actions
* Natural disasters
* Third-party provider disruptions

---

# 19. Changes to These Terms

We may modify these Terms periodically.

Updated versions become effective when published.

Continued use of Coly constitutes acceptance of revised Terms.

---

# 20. Contact Information

[support@colyapp.com](mailto:support@colyapp.com)
`;

export const COOKIES = `# Coly – Cookies & Tracking Technologies Policy

## Professional Health-Tech Legal Version

Effective Date: May 8, 2026
Last Updated: May 8, 2026

This Cookies & Tracking Technologies Policy explains how Coly uses cookies and similar technologies.

---

# 1. Current Tracking Practices

At this time, Coly does not use advertising trackers or third-party behavioral advertising technologies.

Coly currently does not use:

* Advertising cookies
* Behavioral profiling technologies
* Third-party ad networks

---

# 2. Essential Technologies

Coly may use essential technologies required for:

* User authentication
* Account security
* Session management
* Technical functionality
* Infrastructure stability

These technologies are necessary for the app to function properly.

---

# 3. Future Analytics or Tracking

In the future, Coly may implement:

* Analytics tools
* Crash reporting services
* Performance monitoring
* Authentication providers

If such technologies are introduced, this policy will be updated accordingly.

---

# 4. Device Information

The app may automatically collect limited technical information such as:

* Device type
* Operating system
* App version
* Crash diagnostics

This information is used solely for operational and security purposes.

---

# 5. Contact

For questions regarding this policy:

[support@colyapp.com](mailto:support@colyapp.com)
`;

export const PLAY_DISCLOSURE = `# Coly – Google Play Health & AI Disclosure

Coly is a personal wellness and symptom tracking application intended to help users monitor symptoms, medications, food intake, and personal wellness trends related to inflammatory bowel diseases (IBD).

Coly is not a medical device and does not provide medical diagnosis or treatment.

Any AI-generated suggestions or insights are informational only and should not replace professional medical advice.

Coly collects sensitive health-related information solely to provide core app functionality requested by users.

Coly does not sell health data and does not use health information for advertising purposes.

Users may delete their account and associated data directly within the app.
`;

export const WAITLIST_PRIVACY = `# Coly – Waitlist Landing Page Privacy Notice

Effective Date: May 8, 2026

This Waitlist Privacy Notice explains how Coly collects and uses information submitted through the Coly waitlist landing page.

By joining the waitlist, users agree to this notice.

---

# 1. Information We Collect

When users join the Coly waitlist, we may collect:

* Name
* Email address
* Optional feedback or comments submitted voluntarily

---

# 2. How We Use Waitlist Information

We may use submitted information to:

* Provide early access invitations
* Send product updates
* Announce launch information
* Share beta testing opportunities
* Improve the Coly platform
* Respond to inquiries

---

# 3. Marketing Communications

By joining the waitlist, users may receive emails related to:

* Product launches
* Feature announcements
* Beta access
* Updates regarding Coly

Users may unsubscribe at any time using the unsubscribe option included in emails.

---

# 4. Data Sharing

We do not sell waitlist information.

We do not share personal information with advertisers or data brokers.

Information may be processed by infrastructure or hosting providers necessary for operating the waitlist.

---

# 5. Data Retention

We retain waitlist information only for as long as reasonably necessary to operate the waitlist and communicate with interested users.

Users may request deletion at any time.

---

# 6. User Rights

Depending on applicable laws, users may have rights to:

* Access their information
* Correct their information
* Request deletion
* Withdraw consent to communications

Requests may be sent to:

[support@colyapp.com](mailto:support@colyapp.com)

---

# 7. Security

We implement reasonable safeguards intended to protect submitted information.

However, no internet-based system can guarantee absolute security.

---

# 8. Contact

For questions regarding this Waitlist Privacy Notice:

[support@colyapp.com](mailto:support@colyapp.com)
`;

export const APP_PRIVACY_POLICY = `# Coly App – Privacy Policy

## Professional Health-Tech Privacy Policy (Mobile Application)

Effective Date: May 8, 2026
Last Updated: May 8, 2026

This Privacy Policy describes how Coly ("Coly", "we", "our", or "us") collects, uses, stores, processes, protects, and discloses personal information when users access or use the **Coly mobile application** and related in-app services.

Coly is a personal wellness and symptom tracking platform focused on inflammatory bowel diseases (IBD), including Crohn's disease and ulcerative colitis.

By accessing or using the Coly app, users acknowledge and agree to the practices described in this Privacy Policy.

---

# 1. Data Controller

Controller: Hillel Primack
Country: Belgium
Website: [https://colyapp.com/](https://colyapp.com/)
Email: [support@colyapp.com](mailto:support@colyapp.com)

---

# 2. Definitions

For purposes of this Privacy Policy:

* "App" refers to the Coly mobile application.
* "Personal Data" means information relating to an identified or identifiable person.
* "Sensitive Health Data" refers to health-related information voluntarily submitted by users.
* "Processing" means collection, storage, use, disclosure, deletion, or other handling of information.
* "User" means any individual using Coly.
* "GDPR" refers to the General Data Protection Regulation (EU) 2016/679.

---

# 3. Categories of Information We Collect

## 3.1 Account Information

We may collect:

* Name
* Email address
* Encrypted password credentials

## 3.2 Health & Wellness Information

Users may voluntarily provide:

* Symptom tracking information
* Stool and bowel movement tracking
* Medication tracking
* Food diary information
* Wellness notes
* Health-related routines
* Reminder preferences
* AI-generated insight history

## 3.3 Personal Information

We may collect:

* Age
* Height
* Weight

## 3.4 Technical Information

We may automatically collect:

* Device type
* Device operating system
* Application version
* Crash diagnostics
* Error reports
* Technical logs

---

# 4. Special Category Health Data

Certain information processed by Coly may qualify as special category health-related data under GDPR and other privacy laws.

Users explicitly consent to the processing of voluntarily submitted health-related information for wellness tracking purposes.

Coly processes health-related data solely for:

* User-requested tracking
* Personal analytics
* Wellness monitoring
* AI-powered informational summaries
* Historical symptom analysis
* App functionality requested by the user

Coly does not:

* Sell health data
* Use health data for advertising
* Share health information with advertisers
* Share health data with data brokers
* Use health data for unrelated profiling

---

# 5. Legal Bases for Processing (GDPR)

Depending on the circumstances, we process information under one or more lawful bases, including:

## 5.1 Consent

Users voluntarily provide health-related data and consent to its processing.

## 5.2 Performance of Services

Processing may be necessary to provide app functionality requested by users.

## 5.3 Legitimate Interests

We may process limited technical information to:

* Maintain app security
* Improve platform stability
* Detect abuse or fraud
* Resolve technical issues

## 5.4 Legal Obligations

Certain processing may occur where required by law.

---

# 6. How We Use Information

We may use collected information to:

* Provide app functionality
* Enable symptom and wellness tracking
* Generate user-requested analytics
* Provide AI-generated insights
* Improve app performance
* Respond to support requests
* Monitor stability and security
* Detect abuse or unauthorized activity
* Comply with legal obligations

---

# 7. AI Features & Automated Insights

Coly may provide AI-generated suggestions, summaries, informational wellness insights, or automated observations.

These outputs:

* Are informational only
* May contain inaccuracies
* Are not guaranteed to be complete or medically correct
* Should not replace professional medical advice

Users remain solely responsible for healthcare decisions.

---

# 8. Medical Disclaimer

Coly is not a medical device.

Coly does not provide:

* Medical diagnosis
* Clinical treatment
* Emergency assistance
* Professional medical advice

The app is intended solely for personal wellness tracking and informational purposes.

Users should always consult qualified healthcare professionals regarding medical concerns.

---

# 9. Data Sharing & Disclosure

We do not sell personal information.

We may disclose limited information:

* To infrastructure providers necessary for app functionality
* When legally required
* To protect security or legal rights
* During business transfers or restructuring

Third-party providers are contractually expected to process information only for authorized purposes.

---

# 10. Third-Party Infrastructure Providers

Coly currently uses:

* MongoDB

Third-party providers may process limited information required for hosting and infrastructure services.

Additional providers may be introduced in the future, including:

* Authentication providers
* Analytics providers
* Payment processors
* Push notification providers

If services materially change, this Privacy Policy may be updated.

---

# 11. Data Retention

We retain information only for as long as reasonably necessary to:

* Provide services
* Maintain security
* Comply with legal obligations
* Resolve disputes
* Enforce agreements

Deleted information may temporarily remain in backups before permanent deletion.

---

# 12. Account Deletion & Data Removal

Users may delete their account directly within the app.

Following deletion:

* Personal information may be removed
* Health-related tracking information may be deleted
* Associated user records may be permanently erased within a reasonable timeframe unless retention is legally required

Users may also request deletion assistance via:

[support@colyapp.com](mailto:support@colyapp.com)

---

# 13. International Transfers

Coly is available worldwide.

Information may be processed or stored outside a user's country of residence.

Where applicable, we implement reasonable safeguards intended to comply with applicable privacy laws.

---

# 14. Security Measures

We implement commercially reasonable technical and organizational safeguards designed to protect information.

Security measures may include:

* Access controls
* Infrastructure protections
* Authentication safeguards
* Database security controls
* Technical monitoring
* Encrypted communications where applicable

No system can guarantee absolute security.

Users acknowledge risks associated with internet-based services.

---

# 15. User Rights (GDPR)

Users located in the European Economic Area may have rights including:

* Right of access
* Right to correction
* Right to deletion
* Right to portability
* Right to object
* Right to restrict processing
* Right to withdraw consent
* Right to lodge complaints with supervisory authorities

Requests may be submitted to:

[support@colyapp.com](mailto:support@colyapp.com)

---

# 16. Children's Privacy

Coly is not specifically directed toward children under the minimum legal digital consent age applicable in their jurisdiction.

If we become aware that information has been collected without required consent, we may remove such information.

---

# 17. Data Breach Response

In the event of a security incident affecting personal information, we may take actions required under applicable laws, including:

* Investigating incidents
* Securing systems
* Notifying affected users where legally required
* Cooperating with authorities where appropriate

---

# 18. Changes to This Privacy Policy

We may modify this Privacy Policy periodically.

Updated versions become effective when published.

Continued use of Coly after updates constitutes acceptance of revised terms.

---

# 19. Contact Information

For privacy-related questions:

[support@colyapp.com](mailto:support@colyapp.com)
`;

export const APP_TERMS = `# Coly App – Terms & Conditions

## Professional Health-Tech Terms of Use (Mobile Application)

Effective Date: May 8, 2026
Last Updated: May 8, 2026

These Terms & Conditions ("Terms") govern access to and use of the **Coly mobile application** and related in-app services.

By using the Coly app, users agree to these Terms.

If users do not agree, they should discontinue use of the services.

---

# 1. About Coly

Coly is a wellness and symptom tracking platform intended to assist users in monitoring personal wellness information related to inflammatory bowel diseases (IBD), including Crohn's disease and ulcerative colitis.

Coly is not a medical provider or healthcare service.

---

# 2. Eligibility

Users must comply with applicable laws regarding use of digital services.

By using Coly, users represent that:

* Information submitted is accurate
* They will use the app lawfully
* They will not misuse the services

---

# 3. User Accounts

Users may create accounts using email and password authentication.

Users are responsible for:

* Maintaining password confidentiality
* Securing their devices
* Activities occurring under their account

Users must immediately notify us regarding suspected unauthorized access.

---

# 4. Health Information

Users voluntarily provide health-related tracking information.

Users remain responsible for the accuracy and legality of submitted information.

Coly processes submitted information solely for providing requested app functionality.

---

# 5. No Medical Advice

Coly does not provide:

* Medical diagnosis
* Clinical treatment
* Emergency response
* Professional healthcare services

AI-generated suggestions and wellness insights are informational only.

Users should always consult licensed healthcare professionals regarding medical decisions.

---

# 6. AI Services Disclaimer

Coly may provide automated AI-powered insights, summaries, trends, or informational recommendations.

AI-generated outputs:

* May contain inaccuracies
* Are not guaranteed to be correct
* Should not replace professional medical advice
* Are used at the user's own discretion

---

# 7. Acceptable Use

Users agree not to:

* Abuse the platform
* Attempt unauthorized access
* Reverse engineer the application
* Distribute malware
* Interfere with infrastructure
* Use the app unlawfully
* Upload illegal or harmful content

We reserve the right to suspend or terminate accounts violating these Terms.

---

# 8. User Content & Responsibility

Users remain solely responsible for information uploaded or entered into Coly.

Users agree not to upload:

* False medical claims
* Malicious code
* Illegal material
* Content violating third-party rights

---

# 9. Intellectual Property

All app functionality, branding, software, visual elements, logos, and related intellectual property belong to Coly and its owner unless otherwise stated.

Users may not copy, distribute, reproduce, or exploit protected materials without authorization.

---

# 10. Future Paid Features

Coly may introduce future premium subscriptions, paid services, or optional purchases.

If payments are introduced:

* Additional terms may apply
* Pricing will be disclosed before purchase
* Billing terms may be governed by app store providers

---

# 11. Service Availability

We may update, modify, suspend, or discontinue portions of the services at any time.

We do not guarantee uninterrupted or error-free operation.

---

# 12. Limitation of Liability

To the maximum extent permitted by law:

Coly and its owner shall not be liable for:

* Medical outcomes
* Healthcare decisions
* AI inaccuracies
* Data loss
* Service interruptions
* Indirect damages
* User-entered inaccuracies
* Technical failures beyond reasonable control

Users assume responsibility for their use of the app.

---

# 13. Indemnification

Users agree to indemnify and hold harmless Coly and its owner against claims, liabilities, losses, damages, or expenses arising from:

* Violation of these Terms
* Misuse of the services
* Violation of applicable laws
* Reliance on AI-generated outputs

---

# 14. Termination

We may suspend or terminate accounts violating these Terms or applicable laws.

Users may stop using Coly and delete their accounts at any time.

---

# 15. Privacy

Use of Coly is additionally governed by the Coly App Privacy Policy.

---

# 16. Governing Law & Jurisdiction

These Terms are governed by the laws of Belgium.

Applicable disputes shall be subject to competent courts under Belgian law unless otherwise required by applicable consumer protection laws.

---

# 17. Severability

If any provision of these Terms is found unenforceable, remaining provisions shall remain in effect.

---

# 18. Force Majeure

We shall not be liable for delays or failures caused by circumstances beyond reasonable control, including:

* Internet outages
* Infrastructure failures
* Cyberattacks
* Government actions
* Natural disasters
* Third-party provider disruptions

---

# 19. Changes to These Terms

We may modify these Terms periodically.

Updated versions become effective when published.

Continued use of Coly constitutes acceptance of revised Terms.

---

# 20. Contact Information

[support@colyapp.com](mailto:support@colyapp.com)
`;

export const LEGAL_DOCS = {
  privacy: { title: "Privacy Policy", body: PRIVACY_POLICY },
  terms: { title: "Terms & Conditions", body: TERMS },
  cookies: { title: "Cookie Policy", body: COOKIES },
  "play-disclosure": { title: "Google Play Health & AI Disclosure", body: PLAY_DISCLOSURE },
  "waitlist-privacy": { title: "Waitlist Privacy Notice", body: WAITLIST_PRIVACY },
  "app-privacy": { title: "App Privacy Policy", body: APP_PRIVACY_POLICY },
  "app-terms": { title: "App Terms & Conditions", body: APP_TERMS },
};
